import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getTrip, countTripMembers } from '@/services/tripService'
import {
  listBudgetItems,
  createBudgetItem,
  updateBudgetItem,
  deleteBudgetItem,
  type BudgetItem,
} from '@/services/budgetService'
import { BudgetItemForm } from '@/components/BudgetItemForm'
import {
  BUDGET_CATEGORIES,
  type NewBudgetItemFormValues,
  type NewBudgetItemFormInput,
} from '@/utils/schemas'

function budgetItemToFormInput(item: BudgetItem): NewBudgetItemFormInput {
  return {
    label: item.label,
    category: (item.category ?? '') as NewBudgetItemFormInput['category'],
    plannedAmount: String(item.planned_amount),
    actualAmount: String(item.actual_amount),
  }
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value)
}

export function BudgetPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const itemsQuery = useQuery({
    queryKey: ['budget-items', tripId],
    queryFn: () => listBudgetItems(tripId!),
    enabled: !!tripId,
  })

  const memberCountQuery = useQuery({
    queryKey: ['trip-member-count', tripId],
    queryFn: () => countTripMembers(tripId!),
    enabled: !!tripId,
  })

  const addItemMutation = useMutation({
    mutationFn: (values: NewBudgetItemFormValues) =>
      createBudgetItem({
        tripId: tripId!,
        label: values.label,
        category: values.category,
        plannedAmount: values.plannedAmount,
        actualAmount: values.actualAmount,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['budget-items', tripId] })
      setShowAddForm(false)
    },
  })

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, values }: { itemId: string; values: NewBudgetItemFormValues }) =>
      updateBudgetItem(itemId, values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['budget-items', tripId] })
      setEditingItemId(null)
    },
  })

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => deleteBudgetItem(itemId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['budget-items', tripId] })
      setConfirmDeleteId(null)
    },
  })

  if (tripQuery.isLoading || itemsQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('budget.loading')}</p>
  }

  const trip = tripQuery.data
  const currency = trip?.currency ?? 'EUR'
  const items = itemsQuery.data ?? []
  const memberCount = memberCountQuery.data ?? 1

  const plannedTotal = items.reduce((sum, i) => sum + Number(i.planned_amount), 0)
  const actualTotal = items.reduce((sum, i) => sum + Number(i.actual_amount), 0)
  const perPerson = actualTotal / memberCount

  const byCategory = BUDGET_CATEGORIES.map((category) => ({
    category,
    planned: items
      .filter((i) => i.category === category)
      .reduce((sum, i) => sum + Number(i.planned_amount), 0),
    actual: items
      .filter((i) => i.category === category)
      .reduce((sum, i) => sum + Number(i.actual_amount), 0),
  })).filter((c) => c.planned > 0 || c.actual > 0)

  const maxValue = Math.max(1, ...byCategory.flatMap((c) => [c.planned, c.actual]))

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-3.5 font-sans text-sm text-mist"
      >
        {t('budget.backToItinerary')}
      </button>
      <h1 className="font-display text-3xl text-ivory">{t('budget.title')}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">{trip?.title}</div>

      <div className="mt-8 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
        <div className="rounded-lg border border-line bg-panel p-5">
          <div className="mb-2.5 font-mono text-[10.5px] tracking-[0.14em] text-brass-dim uppercase">
            {t('budget.planned')}
          </div>
          <div className="font-display text-3xl text-ivory">
            {formatCurrency(plannedTotal, currency)}
          </div>
        </div>
        <div className="rounded-lg border border-brass-dim bg-panel-2 p-5">
          <div className="mb-2.5 font-mono text-[10.5px] tracking-[0.14em] text-brass-dim uppercase">
            {t('budget.actual')}
          </div>
          <div className="font-display text-3xl text-ivory">
            {formatCurrency(actualTotal, currency)}
          </div>
        </div>
        <div className="rounded-lg border border-line bg-panel p-5">
          <div className="mb-2.5 font-mono text-[10.5px] tracking-[0.14em] text-brass-dim uppercase">
            {t('budget.perPerson')}
          </div>
          <div className="font-display text-3xl text-ivory">
            {formatCurrency(perPerson, currency)}
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-lg border border-line bg-panel p-6">
        <h2 className="mb-5 font-display text-xl text-ivory">{t('budget.expenses')}</h2>

        {items.length === 0 && <p className="mb-4 text-sm text-mist">{t('budget.empty')}</p>}

        {items.map((item) =>
          editingItemId === item.id ? (
            <div key={item.id} className="mb-3.5">
              <BudgetItemForm
                defaultValues={budgetItemToFormInput(item)}
                isPending={updateItemMutation.isPending}
                isError={updateItemMutation.isError}
                submitLabel={t('budget.save')}
                onCancel={() => setEditingItemId(null)}
                onSubmit={(values) => updateItemMutation.mutate({ itemId: item.id, values })}
              />
            </div>
          ) : (
            <div
              key={item.id}
              className="mb-3.5 rounded-md border border-line bg-panel-2 px-4 py-3.5"
            >
              <div className="flex items-center justify-between gap-2.5">
                <span className="font-sans text-[15px] font-medium text-ivory">{item.label}</span>
                {item.category && (
                  <span className="font-mono text-[10px] tracking-wide text-brass-dim uppercase">
                    {t(`budget.category.${item.category}`)}
                  </span>
                )}
              </div>
              <div className="mt-1 font-mono text-xs text-mist">
                {formatCurrency(Number(item.actual_amount), currency)} /{' '}
                {formatCurrency(Number(item.planned_amount), currency)}
              </div>

              {confirmDeleteId === item.id ? (
                <div className="mt-2.5 rounded-r border-l-[3px] border-line border-l-brass-warm bg-panel px-3 py-2.5">
                  {deleteItemMutation.isError && (
                    <p role="alert" className="mb-2 text-sm text-brass-warm">
                      {t('budget.deleteError')}
                    </p>
                  )}
                  <p className="mb-2 text-sm text-ivory">{t('budget.confirmDeleteItem')}</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="rounded border border-line-strong px-3 py-1.5 font-sans text-xs text-ivory"
                    >
                      {t('budget.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteItemMutation.mutate(item.id)}
                      disabled={deleteItemMutation.isPending}
                      className="rounded border border-brass-warm px-3 py-1.5 font-sans text-xs font-medium text-brass-warm disabled:opacity-50"
                    >
                      {t('budget.confirmDeleteItemButton')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2.5 flex gap-4">
                  <button
                    type="button"
                    onClick={() => setEditingItemId(item.id)}
                    className="font-sans text-xs text-mist hover:text-brass"
                  >
                    {t('budget.editItem')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(item.id)}
                    className="font-sans text-xs text-mist hover:text-brass-warm"
                  >
                    {t('budget.deleteItem')}
                  </button>
                </div>
              )}
            </div>
          ),
        )}

        {showAddForm ? (
          <BudgetItemForm
            isPending={addItemMutation.isPending}
            isError={addItemMutation.isError}
            submitLabel={t('budget.addExpense')}
            onCancel={() => setShowAddForm(false)}
            onSubmit={(values) => addItemMutation.mutate(values)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="mt-4 rounded-md border border-dashed border-line-strong px-4 py-3 text-left font-sans text-sm text-mist hover:border-brass hover:text-brass"
          >
            {t('budget.addExpense')}
          </button>
        )}
      </div>

      <div className="mt-8 rounded-lg border border-line bg-panel p-6">
        <h2 className="mb-5 font-display text-xl text-ivory">{t('budget.byCategory')}</h2>

        {byCategory.map((c) => (
          <div key={c.category} className="mb-5">
            <div className="mb-2 flex justify-between">
              <span className="font-sans text-sm text-ivory">
                {t(`budget.category.${c.category}`)}
              </span>
              <span className="font-mono text-xs text-mist">
                {formatCurrency(c.actual, currency)} / {formatCurrency(c.planned, currency)}
              </span>
            </div>
            <div className="relative h-5">
              <div
                className="absolute inset-y-0 left-0 rounded bg-panel-2"
                style={{ width: `${(c.planned / maxValue) * 100}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 rounded bg-brass opacity-90"
                style={{ width: `${(c.actual / maxValue) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
